import {
	ChakraProvider,
	createSystem,
	defaultConfig,
	defineConfig
} from '@chakra-ui/react'
import { ReactKeycloakProvider } from '@react-keycloak/web'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import 'react-loading-skeleton/dist/skeleton.css'
import { Provider } from 'react-redux'

import { store } from '@/store'

import App from './App'
import { clearSentryUser, syncSentryUser } from './auth'
import keycloak from './auth/keycloak'
import { DraftsProvider } from './features/Home/components/shotlistUpload/DraftsContext'
import './index.scss'

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration()
	],
	tracesSampleRate:
		Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
	replaysSessionSampleRate:
		Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) || 0.1,
	replaysOnErrorSampleRate:
		Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) || 1.0,
	environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
	tracePropagationTargets: [
		'localhost',

		// Viory
		/^https:\/\/stenogram\.viory\.video\/api\//,
		/^https:\/\/stenogram\.dev\.viory\.video\/api\//,

		// Ruptly
		/^https:\/\/stenogram\.ruptly\.video\/api\//,
		/^https:\/\/stenogram\.dev\.ruptly\.video\/api\//
	]
})

Sentry.getClient()?.on('spanStart', span => {
	const email = keycloak.authenticated ? keycloak.tokenParsed?.email : undefined

	if (typeof email !== 'string' || !email.trim()) {
		return
	}

	span.setAttribute('user.id', email.trim())
})

const config = defineConfig({
	theme: {
		tokens: {
			colors: {}
		}
	}
})

const system = createSystem(defaultConfig, config)

function Root() {
	return (
		<Provider store={store}>
			<ChakraProvider value={system}>
				<DraftsProvider>
					<App />
				</DraftsProvider>
			</ChakraProvider>
		</Provider>
	)
}

ReactDOM.createRoot(document.getElementById('root')!).render(
	<ReactKeycloakProvider
		authClient={keycloak}
		initOptions={{
			onLoad: 'login-required',
			redirectUri: window.location.href
		}}
		onEvent={event => {
			switch (event) {
				case 'onReady':
				case 'onAuthSuccess':
				case 'onAuthRefreshSuccess':
					syncSentryUser()
					break

				case 'onAuthLogout':
				case 'onAuthError':
					clearSentryUser()
					break
			}
		}}
		onTokens={() => {
			syncSentryUser()
		}}
	>
		<Root />
	</ReactKeycloakProvider>
)
